/**
 * Desktop Sidebar Navigation
 * Google Photos-inspired Vertical Navigation Pattern
 *
 * Research: Desktop sidebar with vertical navigation
 * - Fixed left, 256px width (always visible on desktop)
 * - Positioned below header bar
 * - Navigation items with icons
 * - Settings at bottom
 * - Active state: left border indicator
 * - WCAG AA accessibility
 */

import React from 'react';
import { Sword, Backpack, ScrollText, Settings } from 'lucide-react';
import { cn } from '../../lib/utils';

const TABS = [
  { id: 'combat', label: 'Actions', icon: Sword },
  { id: 'inventory', label: 'Inventory', icon: Backpack },
  { id: 'character', label: 'Character', icon: ScrollText },
];

export default function DesktopSidebar({
  activeTab,
  onTabChange,
  onSettingsClick,
  character,
}) {
  return (
    <aside
      className="hidden lg:flex flex-col w-sidebar bg-surface-1 fixed left-0 top-[73px] bottom-0 z-40 shadow-2xl"
      role="navigation"
      aria-label="Character sheet sections"
    >
      {/* Character Identity Card */}
      <div className="px-4 py-4 space-y-2">
        {/* Character Name */}
        <h2 className="text-heading-lg text-red-400 font-bold truncate font-serif">
          {character?.name || 'Character'}
        </h2>

        {/* Character Details */}
        <div className="text-sm space-y-1 text-gray-300">
          {/* Level, Class, Subclass */}
          <div className="font-medium">
            <span className="font-bold text-red-400">Level {character?.level || 1}</span>
            {' '}
            <span className="text-gray-100">{character?.class ? character.class.charAt(0).toUpperCase() + character.class.slice(1) : 'Class'}</span>
            {character?.subclass && (
              <>
                <span className="text-gray-100">: </span>
                <span className="text-gray-400">
                  {character.subclass
                    .split('-')
                    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ')
                  }
                </span>
              </>
            )}
          </div>

          {/* Ancestry & Community */}
          <div className="text-gray-400 text-xs">
            {typeof character?.ancestry === 'string'
              ? character.ancestry.replace(/([A-Z])/g, ' $1').trim()
              : Array.isArray(character?.ancestry)
                ? character.ancestry.map(a => a.charAt(0).toUpperCase() + a.slice(1)).join('/')
                : character?.ancestry || 'Ancestry'}
            {character?.community && (
              <>
                {' - '}
                {character.community.charAt(0).toUpperCase() + character.community.slice(1)}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Navigation Items */}
      <nav className="flex-1 py-2 px-3" role="tablist">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;

          return (
            <button
              key={tab.id}
              role="tab"
              aria-selected={isActive}
              aria-controls={`${tab.id}-panel`}
              onClick={() => onTabChange(tab.id)}
              className={cn(
                // Base styles - Google Photos spacing (more generous)
                'w-full flex items-center gap-4 px-4 py-4 mb-1',
                'text-left transition-all duration-200',
                'relative rounded-lg',
                // Focus styles (WCAG AA)
                'focus:outline-none focus:ring-2 focus:ring-red-700 focus:ring-inset',
                // Active/hover states with red gaming aesthetics
                isActive
                  ? 'text-red-400 bg-red-900/20 border border-red-700/50'
                  : 'text-gray-400 hover:text-red-300 hover:bg-red-900/10'
              )}
            >
              {/* Active indicator (left border) with red accent */}
              {isActive && (
                <div
                  className="absolute left-0 top-1 bottom-1 w-1 bg-red-700 rounded-r-full shadow-lg shadow-red-700/50"
                  aria-hidden="true"
                />
              )}

              {/* Icon - Larger for Google Photos feel (28px) */}
              <Icon className="w-7 h-7 flex-shrink-0" />

              {/* Label */}
              <span className="text-nav font-medium">{tab.label}</span>
            </button>
          );
        })}
      </nav>

      {/* Settings - Bottom */}
      {onSettingsClick && (
        <div className="px-3 py-3">
          <button
            onClick={onSettingsClick}
            className={cn(
              'w-full flex items-center gap-4 px-4 py-4',
              'text-left text-gray-400 hover:text-red-300',
              'hover:bg-red-900/10 rounded-lg',
              'transition-all duration-200',
              'focus:outline-none focus:ring-2 focus:ring-red-700'
            )}
          >
            <Settings className="w-7 h-7 flex-shrink-0" />
            <span className="text-nav font-medium">Settings</span>
          </button>
        </div>
      )}
    </aside>
  );
}
